import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { InTimePipe } from 'src/app/_shared/pipes/in-time.pipe';
import { createPlotlyChart, deletePlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-sohc-over-time-chart',
  templateUrl: './sohc-over-time-chart.component.html',
  styleUrls: ['./sohc-over-time-chart.component.scss']
})
export class SohcOverTimeChartComponent implements OnChanges {
  @Input() filteredVehiclesData!: any[];
  @Input() selectedVehicles!: any[];
  @Input() dataLoadingError: boolean = false;
  deviceSerialNumber: string[] = [];
  private colorMap: { [key: string]: string } = {};
  xRange: [Date, Date] | undefined = undefined;
  @Input() sohTrajectories: any;
  @Input() errorMessageSohTrajectories: boolean = false;
  @Input() dataLoadingSohTrajectories: boolean = false;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoadingSohTrajectories = true;
    this.errorMessageSohTrajectories = false;
    const currentSelectedVehicles = changes['selectedVehicles']?.currentValue ?? this.selectedVehicles;
    const sohChanged = !!changes['sohTrajectories'];
    
    if (Array.isArray(currentSelectedVehicles) && currentSelectedVehicles.length > 0 && sohChanged) {
      if (!this.sohTrajectories || Object.keys(this.sohTrajectories).length === 0) {
        this.dataLoadingSohTrajectories = false;
        this.errorMessageSohTrajectories = true;
        deletePlotlyChart('sohc-over-time-chart');
        return;
      }
      
      this.dataLoadingSohTrajectories = true;
      this.errorMessageSohTrajectories = false;
      this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
      
      this.initializeColorMap();
      const allPlotlyData: any[] = [];
      const shapes: any[] = [];
      const annotations: any[] = [];

      // Global variables for the X range
      let globalEstimationEnd: number | null = null;
      let globalPredictionEnd: number | null = null;

      Object.keys(this.sohTrajectories).forEach((deviceSerialNumberId: string) => {
        const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);
        const deviceData = this.sohTrajectories[deviceSerialNumberId];
        const estimationData = this.sortByTime(deviceData.estimations || []);
        const predictionData = this.sortByTime(deviceData.predictions || []);

        // Global variables for the X range calculation
        if (estimationData.length > 0) {
          const lastEstimation = estimationData[estimationData.length - 1];
          const estimationEnd = new Date(lastEstimation.timestamp).getTime();
          if (!globalEstimationEnd || estimationEnd > globalEstimationEnd) {
            globalEstimationEnd = estimationEnd;
          }
        }
        if (predictionData.length > 0) {
          const lastPrediction = predictionData[predictionData.length - 1];
          const predictionEnd = new Date(lastPrediction.timestamp).getTime();
          if (!globalPredictionEnd || predictionEnd > globalPredictionEnd) {
            globalPredictionEnd = predictionEnd;
          }
        }

        // Trace estimation (only if there is data)
        if (estimationData.length > 0) {
          const estimationTrace = {
            x: estimationData.map((point: any) => point.timestamp),
            y: estimationData.map((point: any) => point.batteryStateOfHealth),
            type: 'scatter',
            mode: 'lines',
            name: vehicle.name + ' Estimation',
            line: { dash: 'solid' },
            marker: {
              color: this.colorMap[deviceSerialNumberId]
            },
            showlegend: true
          };
          allPlotlyData.push(estimationTrace);

          // Marker for last estimation
          const lastEstimation = estimationData[estimationData.length - 1];
          allPlotlyData.push({
            x: [lastEstimation.timestamp],
            y: [lastEstimation.batteryStateOfHealth],
            type: 'scatter',
            mode: 'markers+text',
            name: vehicle.name + ' Last Estimation',
            marker: {
              color: this.colorMap[deviceSerialNumberId],
              size: 16,
              symbol: 'circle'
            },
            showlegend: false
          });

          // Vertical dashed line for last estimation
          shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'y',
            x0: lastEstimation.timestamp,
            y0: 0,
            x1: lastEstimation.timestamp,
            y1: lastEstimation.batteryStateOfHealth,
            line: {
              dash: 'dot',
              color: this.colorMap[deviceSerialNumberId],
              width: 1
            }
          });
        }

        // Trace prediction & marker (only if there is predictionData)
        if (predictionData.length > 0) {
          const lastPrediction = predictionData[predictionData.length - 1];
          const lastPredictionX = lastPrediction.timestamp;
          const lastPredictionY = lastPrediction.batteryStateOfHealth;

          // Arrow marker only if there are at least 2 predictions
          if (predictionData.length > 1) {
            const beforeLastPrediction = predictionData[predictionData.length - 2];
            allPlotlyData.push({
              x: [beforeLastPrediction.timestamp],
              y: [beforeLastPrediction.batteryStateOfHealth],
              type: 'scatter',
              mode: 'markers',
              marker: {
                color: this.colorMap[deviceSerialNumberId],
                size: 8,
                symbol: 'arrow-right'
              },
              showlegend: false
            });
          }

          // Marker for last prediction
          allPlotlyData.push({
            x: [lastPredictionX],
            y: [lastPredictionY],
            type: 'scatter',
            mode: 'markers',
            name: vehicle.name + ' Prediction',
            marker: {
              color: this.colorMap[deviceSerialNumberId],
              size: 16,
              symbol: 'circle-open',
              line: { width: 4 }
            },
            showlegend: false
          });

          // Horizontal dashed line from last estimation to last prediction
          if (estimationData.length > 0) {
            const lastEstimation = estimationData[estimationData.length - 1];
            shapes.push({
              type: 'line',
              xref: 'x',
              yref: 'y',
              x0: lastEstimation.timestamp,
              y0: lastPredictionY,
              x1: lastPredictionX,
              y1: lastPredictionY,
              line: {
                dash: 'dot',
                color: this.colorMap[deviceSerialNumberId],
                width: 1
              }
            });
          }
        }
      });

      // Legend entries for prediction and estimation
      const legendPredictionDot = {
        x: [null],
        y: [null],
        type: 'scatter',
        mode: 'markers',
        name: 'Last Estimation',
        marker: {
          size: 16,
          symbol: 'dot',
          color: 'black'
        },
        showlegend: true
      }

      const inTime = new InTimePipe();

      const legendEstimationDot = {
        x: [null],
        y: [null],
        type: 'scatter',
        mode: 'markers',
        name: inTime.transform(this.selectedVehicles[0]?.currentVehicleInsights.predictionDays, true) + ' Prediction',
        marker: {
          size: 16,
          symbol: 'circle-open',
          color: 'black',
          line: {
            width: 4
          }
        },
        showlegend: true
      }

      allPlotlyData.push(legendPredictionDot, legendEstimationDot);

      // Calculating the global X range
      const sixMonthsMs = 182 * 24 * 60 * 60 * 1000;
      const oneMonth = 30 * 24 * 60 * 60 * 1000

      if (globalEstimationEnd && globalPredictionEnd) {
        const xMin = globalEstimationEnd - sixMonthsMs;
        const xMax = globalPredictionEnd + oneMonth;
        this.xRange = [new Date(xMin), new Date(xMax)];
      }

      const yRange = this.getYRange(allPlotlyData);

      const layout = {
        title: 'State of Health over time',
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
        xaxis: {
          title: 'Time [Months]',
          range: this.xRange
          /* range: this.selectedVehicles.length == 1 ? this.xRange : undefined */
        },
        yaxis: {
          title: 'State of Health [%]',
          range: yRange
        },
        showlegend: true, 
        legend: {
          orientation: 'h',
          y: -0.3,
          xanchor: 'center',
          x: 0.5,
          font: {
            family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
            size: 13,
          },
          itemclick: false,
          itemdoubleclick: false,
        },
        shapes: shapes,
        annotations: annotations
      };

      createPlotlyChart('sohc-over-time-chart', allPlotlyData, layout);
      this.dataLoadingSohTrajectories = false;
      
    } else if (this.dataLoadingError) {
      this.dataLoadingSohTrajectories = false;
      this.errorMessageSohTrajectories = true;
    }
  }

  sortByTime(data: any[]): any[] {
    return data.slice().sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  private getYRange(allPlotlyData: any[]): [number, number] {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    allPlotlyData.forEach(trace => {
      if (Array.isArray(trace.y)) {
        trace.y.forEach((val: number) => {
          if (typeof val === 'number' && !isNaN(val)) {
            min = Math.min(min, val);
            max = Math.max(max, val);
          }
        });
      }
    });
    // Default range if no data
    if (!isFinite(min) || !isFinite(max)) {
      min = 80;
      max = 100;
    }
    return [min - 5, max + 5];
  }

}
